import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { MaskedLines } from "@/lib/motion";

const SLIDES = [
  { img: "/assets/slider-2022-speaker.jpg", caption: "Voices shaping organic business.", credit: "OFIC 2022 · From the archives", pos: "60% 40%" },
  { img: "/assets/slider-2022-inauguration.jpg", caption: "Coming together for a greener future.", credit: "OFIC 2022 · From the archives", pos: "50% 45%" },
  { img: "/assets/slider-conference-delegates.jpg", caption: "Ideas. Dialogue. New possibilities.", credit: "Conference highlights · From the archives", pos: "50% 55%" },
  { img: "/assets/slider-ofic-2019.jpg", caption: "Perspectives that inspire. OFIC 2019.", credit: "OFIC 2019 · From the archives", pos: "45% 40%" },
  { img: "/assets/slider-ofic-2019-awards.jpg", caption: "Celebrating excellence. OFIC 2019.", credit: "OFIC 2019 · From the archives", pos: "50% 45%" },
];

export default function Hero({ onBrochure }) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const reduced = useReducedMotion();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "12%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  useEffect(() => {
    if (paused || reduced) return undefined;
    const timer = setInterval(() => setIndex((i) => (i + 1) % SLIDES.length), 5500);
    return () => clearInterval(timer);
  }, [paused, reduced]);

  const slide = SLIDES[index];

  return (
    <section ref={heroRef} data-testid="hero" className="relative overflow-hidden bg-[#f3f3e9]">
      <div className="wrap grid items-stretch gap-10 pb-16 pt-12 lg:grid-cols-[1.05fr_1fr] lg:gap-14 lg:pb-24 lg:pt-16">
        <motion.div style={{ y: contentY }} className="flex flex-col justify-center">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-8 flex items-center gap-3"
            data-testid="hero-edition-badge"
          >
            <span className="rounded-full px-4 py-1.5" style={{ background: "var(--forest)", color: "var(--lime)" }}>
              <sup className="mr-0.5 text-[10px]">3</sup>
              <span className="text-[11px] font-extrabold tracking-[0.18em]">RD EDITION</span>
            </span>
            <span className="eyebrow text-[#5c6b5a]">OFIC 2026</span>
          </motion.div>

          <h1 className="font-display text-[42px] leading-[1.02] sm:text-6xl lg:text-[72px]" aria-label="Good food. Great connections. Greener futures.">
            <MaskedLines
              lines={["Good food.", "Great connections.", <em key="em" className="italic text-[#123d30]">Greener futures.</em>]}
              delay={0.15}
            />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="mt-6 text-[13px] font-extrabold tracking-[0.2em]"
          >
            ORGANIC FOOD INDIA CONCLAVE 2026
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="mt-3 max-w-md text-base leading-relaxed text-[#5c6b5a] sm:text-lg"
          >
            An Event on Organic Agri Food Business — Opportunities Amidst Challenges.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.9 }}
            className="mt-9 flex flex-wrap items-center gap-5"
          >
            <Link to="/register" data-testid="hero-cta-register" className="ofic-btn ofic-btn-lime">
              Be part of OFIC 2026 <span className="arrow">↗</span>
            </Link>
            <button data-testid="hero-brochure-link" onClick={onBrochure} className="text-link">
              Explore the brochure ↓
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.0 }}
            className="mt-12 grid max-w-lg grid-cols-2 gap-px overflow-hidden rounded-lg"
            style={{ background: "var(--line)" }}
            data-testid="hero-event-highlight"
          >
            <div className="bg-[#fbfbf4] p-5">
              <span className="eyebrow text-[10px] text-[#5c6b5a]">Mark your calendar</span>
              <strong className="mt-1 block font-display text-xl">
                11 December <span className="text-[#5c6b5a]">2026</span>
              </strong>
            </div>
            <div className="bg-[#fbfbf4] p-5">
              <span className="eyebrow text-[10px] text-[#5c6b5a]">Meet us in Mumbai</span>
              <strong className="mt-1 block font-display text-xl">Ginger Hotel, Mumbai</strong>
            </div>
          </motion.div>

          <motion.a
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 1.1 }}
            href="https://www.teflas.com"
            target="_blank"
            rel="noreferrer"
            className="mt-10 inline-flex items-center gap-3 no-underline"
            data-testid="hero-organiser-link"
          >
            <span className="eyebrow text-[10px] text-[#5c6b5a]">Organised by</span>
            <img src="/assets/teflas-mark.png" alt="Tefla's" className="h-8 w-auto" />
          </motion.a>
        </motion.div>

        <div className="relative" data-testid="hero-slider">
          <motion.div
            initial={{ opacity: 0, scale: 0.985 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="relative h-[420px] overflow-hidden rounded-2xl sm:h-[520px] lg:h-[calc(100%-8px)]"
            style={{ border: "1px solid var(--line)" }}
          >
            <motion.img
              key={slide.img}
              src={slide.img}
              alt={slide.caption + " — " + slide.credit}
              style={{ objectPosition: slide.pos }}
              className="absolute inset-0 h-full w-full object-cover"
              initial={{ opacity: 0.4, scale: 1.04 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.1, ease: "easeOut" }}
            />
            <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(11,43,33,.72) 0%, rgba(11,43,33,.06) 45%)" }} />
            <div className="absolute left-5 top-5">
              <span className="rounded-full px-3 py-1 text-[10px] font-extrabold tracking-[0.2em]" style={{ background: "var(--lime)", color: "var(--forest)" }}>
                OFIC 2026
              </span>
            </div>
            <div className="absolute inset-x-5 bottom-5 flex items-end justify-between gap-4">
              <div>
                <p className="font-display text-lg text-[#f3f3e9] sm:text-2xl" data-testid="hero-slider-caption">{slide.caption}</p>
                <small className="text-[11px] text-[#d7ef8b]">{slide.credit}</small>
              </div>
              <div className="flex items-center gap-2">
                <button data-testid="hero-slider-prev" aria-label="Previous slide" onClick={() => setIndex((index - 1 + SLIDES.length) % SLIDES.length)} className="grid h-9 w-9 place-items-center rounded-full text-[#f3f3e9]" style={{ border: "1px solid rgba(243,243,233,.5)" }}>←</button>
                <button data-testid="hero-slider-pause" aria-label={paused ? "Play slideshow" : "Pause slideshow"} onClick={() => setPaused(!paused)} className="grid h-9 w-9 place-items-center rounded-full text-[#f3f3e9]" style={{ border: "1px solid rgba(243,243,233,.5)" }}>{paused ? "▶" : "Ⅱ"}</button>
                <button data-testid="hero-slider-next" aria-label="Next slide" onClick={() => setIndex((index + 1) % SLIDES.length)} className="grid h-9 w-9 place-items-center rounded-full text-[#f3f3e9]" style={{ border: "1px solid rgba(243,243,233,.5)" }}>→</button>
              </div>
            </div>
            <div className="absolute right-5 top-5 flex flex-col gap-1.5">
              {SLIDES.map((s, i) => (
                <button
                  key={s.img}
                  data-testid={`hero-slider-dot-${i}`}
                  aria-label={`Go to slide ${i + 1}`}
                  onClick={() => setIndex(i)}
                  className="h-1.5 rounded-full transition-all"
                  style={{ width: i === index ? 22 : 10, background: i === index ? "var(--lime)" : "rgba(243,243,233,.5)" }}
                />
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
